import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Search, Eye, Edit, X, Loader2, Calendar, MapPin, DollarSign, Users, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobPostings = () => {
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [formData, setFormData] = useState({
    company_id: '',
    title: '',
    description: '',
    requirements: '',
    salary_range_min: '',
    salary_range_max: '',
    job_type: 'Full Time',
    location: '',
    vacancies: 1,
    eligible_courses: [],
    min_percentage: '',
    application_deadline: '',
    skills_required: []
  });
  const [skillInput, setSkillInput] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const token = localStorage.getItem('token');

  const jobTypes = ['Full Time', 'Part Time', 'Internship', 'Contract'];

  useEffect(() => {
    fetchJobs();
    fetchCompanies();
    fetchCourses();
  }, [statusFilter]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/jobs?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Approve Job
  const handleApproveJob = async (jobId) => {
    try {
      await axios.put(`${API_URL}/api/placement/jobs/${jobId}`, 
        { status: 'active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Job approved successfully!');
      fetchJobs();
    } catch (error) {
      console.error('Error approving job:', error);
      toast.error('Failed to approve job');
    }
  };

  // Reject Job
  const handleRejectJob = async (jobId) => {
    try {
      await axios.put(`${API_URL}/api/placement/jobs/${jobId}`, 
        { status: 'rejected' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Job rejected');
      fetchJobs();
    } catch (error) {
      console.error('Error rejecting job:', error);
      toast.error('Failed to reject job');
    }
  };

  // View Job Details
  const handleViewJob = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        salary_range_min: formData.salary_range_min ? parseFloat(formData.salary_range_min) : null,
        salary_range_max: formData.salary_range_max ? parseFloat(formData.salary_range_max) : null,
        min_percentage: formData.min_percentage ? parseFloat(formData.min_percentage) : null,
        vacancies: parseInt(formData.vacancies)
      };

      if (editingJob) {
        await axios.put(`${API_URL}/api/placement/jobs/${editingJob.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API_URL}/api/placement/jobs?company_id=${formData.company_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job posted successfully');
      }
      setShowModal(false);
      resetForm();
      fetchJobs();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      title: '',
      description: '',
      requirements: '',
      salary_range_min: '',
      salary_range_max: '',
      job_type: 'Full Time',
      location: '',
      vacancies: 1,
      eligible_courses: [],
      min_percentage: '',
      application_deadline: '',
      skills_required: []
    });
    setEditingJob(null);
    setSkillInput('');
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills_required.includes(skillInput.trim())) {
      setFormData({
        ...formData,
        skills_required: [...formData.skills_required, skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({
      ...formData,
      skills_required: formData.skills_required.filter(s => s !== skill)
    });
  };

  const toggleCourse = (courseName) => {
    const courses = formData.eligible_courses.includes(courseName)
      ? formData.eligible_courses.filter(c => c !== courseName)
      : [...formData.eligible_courses, courseName];
    setFormData({ ...formData, eligible_courses: courses });
  };

  const filteredJobs = jobs.filter(j =>
    j.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    j.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Not Disclosed';
    const format = (n) => n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${n.toLocaleString('en-IN')}`;
    if (min && max) return `${format(min)} - ${format(max)}`;
    if (min) return `${format(min)}+`;
    return `Up to ${format(max)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="job-postings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Postings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage job opportunities for students</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Post New Job
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {['active', 'closed', 'pending_approval'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-4">
        {filteredJobs.map(job => (
          <div key={job.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    job.job_type === 'Full Time' ? 'bg-green-100 text-green-700' :
                    job.job_type === 'Internship' ? 'bg-purple-100 text-purple-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {job.job_type}
                  </span>
                  <span className="text-xs text-gray-500">{job.job_code}</span>
                </div>
                <p className="text-blue-600 font-medium mb-3">{job.company_name}</p>
                
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                  {job.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> {job.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4" /> {formatSalary(job.salary_range_min, job.salary_range_max)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" /> {job.vacancies} {job.vacancies > 1 ? 'positions' : 'position'}
                  </span>
                  {job.application_deadline && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" /> Deadline: {job.application_deadline}
                    </span>
                  )}
                </div>

                {job.skills_required?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {job.skills_required.map(skill => (
                      <span key={skill} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-2 ml-4">
                <div className="flex gap-2">
                  {/* View Button */}
                  <button
                    onClick={() => handleViewJob(job)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4 text-gray-500" />
                  </button>
                  
                  {/* Approve/Reject Buttons for Pending Jobs */}
                  {statusFilter === 'pending_approval' && (
                    <>
                      <button
                        onClick={() => handleApproveJob(job.id)}
                        className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                        title="Approve Job"
                      >
                        <CheckCircle className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleRejectJob(job.id)}
                        className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                        title="Reject Job"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
                <div className="text-right text-sm">
                  <p className="text-gray-500">{job.applications_count || 0} applications</p>
                  <p className="text-green-600">{job.selected_count || 0} selected</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredJobs.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg">
          <Briefcase className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No jobs found</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">{editingJob ? 'Edit Job' : 'Post New Job'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <select
                    required
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Company</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Software Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select
                    value={formData.job_type}
                    onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {jobTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Bangalore, Kolkata"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Salary (Annual)</label>
                  <input
                    type="number"
                    value={formData.salary_range_min}
                    onChange={(e) => setFormData({ ...formData, salary_range_min: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 300000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Salary (Annual)</label>
                  <input
                    type="number"
                    value={formData.salary_range_max}
                    onChange={(e) => setFormData({ ...formData, salary_range_max: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 500000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vacancies</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.vacancies}
                    onChange={(e) => setFormData({ ...formData, vacancies: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
                  <input
                    type="date"
                    value={formData.application_deadline}
                    onChange={(e) => setFormData({ ...formData, application_deadline: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
                <textarea
                  required
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
                <textarea
                  rows="3"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add skill and press Enter"
                  />
                  <button type="button" onClick={addSkill} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.skills_required.map(skill => (
                    <span key={skill} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      {skill}
                      <button type="button" onClick={() => removeSkill(skill)} className="hover:text-blue-900">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Eligible Courses</label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                  {courses.map(course => (
                    <button
                      key={course.id}
                      type="button"
                      onClick={() => toggleCourse(course.name)}
                      className={`px-3 py-1 rounded-full text-sm ${
                        formData.eligible_courses.includes(course.name)
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {course.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingJob ? 'Update' : 'Post'} Job
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Job Detail Modal */}
      {showDetailModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">{selectedJob.title}</h2>
                  <p className="text-blue-600">{selectedJob.company_name}</p>
                </div>
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Job Info Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Job Type</p>
                  <p className="font-medium">{selectedJob.job_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{selectedJob.location || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Salary</p>
                  <p className="font-medium">{formatSalary(selectedJob.salary_range_min, selectedJob.salary_range_max)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vacancies</p>
                  <p className="font-medium">{selectedJob.vacancies}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deadline</p>
                  <p className="font-medium">{selectedJob.application_deadline || 'No deadline'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`px-2 py-1 rounded text-sm ${
                    selectedJob.status === 'active' ? 'bg-green-100 text-green-700' :
                    selectedJob.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedJob.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Job Description</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              {/* Requirements */}
              {selectedJob.requirements && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Requirements</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedJob.requirements}</p>
                </div>
              )}

              {/* Skills */}
              {selectedJob.skills_required?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Required Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.skills_required.map(skill => (
                      <span key={skill} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Eligible Courses */}
              {selectedJob.eligible_courses?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Eligible Courses</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.eligible_courses.map(course => (
                      <span key={course} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {course}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons for Pending Approval */}
              {selectedJob.status === 'pending_approval' && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => { handleApproveJob(selectedJob.id); setShowDetailModal(false); }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-5 w-5" /> Approve Job
                  </button>
                  <button
                    onClick={() => { handleRejectJob(selectedJob.id); setShowDetailModal(false); }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                  >
                    <XCircle className="h-5 w-5" /> Reject Job
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPostings;
